'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { X, Tag, Calendar, Star, ChevronRight } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface PopupConfig {
  popup_enabled: string;
  popup_title: string;
  popup_text: string;
  popup_image_url: string;
  popup_link_url: string;
  popup_category: string;
}

const DEFAULT_CONFIG: PopupConfig = {
  popup_enabled: 'false',
  popup_title: 'This Month Special Offer!',
  popup_text: 'Enroll now and get 20% off on all computer courses. Limited seats available!',
  popup_image_url: '',
  popup_link_url: '/sign-up-login-screen',
  popup_category: 'offer',
};

const CATEGORY_STYLES: Record<string, { label: string; icon: React.ReactNode; color: string; bg: string }> = {
  offer: {
    label: 'Special Offer',
    icon: <Tag size={13} />,
    color: '#fff',
    bg: '#E53E3E',
  },
  event: {
    label: 'Event',
    icon: <Calendar size={13} />,
    color: '#fff',
    bg: '#2563EB',
  },
  special: {
    label: 'This Month Special',
    icon: <Star size={13} />,
    color: '#fff',
    bg: '#D97706',
  },
};

export default function PopupNotification() {
  const [visible, setVisible] = useState(false);
  const [config, setConfig] = useState<PopupConfig>(DEFAULT_CONFIG);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('site_settings')
          .select('key, value')
          .in('key', ['popup_enabled', 'popup_title', 'popup_text', 'popup_image_url', 'popup_link_url', 'popup_category']);
        if (error) return;
        if (data && data.length > 0) {
          const map: Partial<PopupConfig> = {};
          data.forEach((row: { key: string; value: string }) => {
            (map as Record<string, string>)[row.key] = row.value || '';
          });
          setConfig(prev => ({ ...prev, ...map }));
        }
      } catch {
        // use defaults
      } finally {
        setLoaded(true);
      }
    };
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!loaded) return;
    if (config.popup_enabled !== 'true') return;
    // Show popup after 1.5s delay
    const timer = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(timer);
  }, [loaded, config.popup_enabled]);

  if (!visible) return null;

  const category = CATEGORY_STYLES[config.popup_category] || CATEGORY_STYLES['offer'];
  const hasImage = !!config.popup_image_url;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
      onClick={() => setVisible(false)}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-sm animate-popup"
        style={{ border: '1px solid #e5e7eb' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header badge */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ background: category.bg }}
        >
          <div className="flex items-center gap-2">
            <span style={{ color: category.color }}>{category.icon}</span>
            <span className="text-sm font-bold" style={{ color: category.color }}>
              {category.label}
            </span>
          </div>
          <button
            onClick={() => setVisible(false)}
            className="transition-opacity hover:opacity-70"
            style={{ color: category.color }}
            aria-label="Close popup"
          >
            <X size={18} />
          </button>
        </div>

        {/* Image */}
        {hasImage && (
          <div className="w-full" style={{ maxHeight: '200px', overflow: 'hidden' }}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={config.popup_image_url}
              alt={config.popup_title}
              className="w-full object-cover"
              style={{ maxHeight: '200px' }}
            />
          </div>
        )}

        {/* Content */}
        <div className="px-5 py-4">
          <h3 className="text-lg font-bold text-gray-900 mb-2 leading-snug">
            {config.popup_title}
          </h3>
          <p className="text-sm text-gray-600 mb-4 leading-relaxed">
            {config.popup_text}
          </p>

          <Link
            href={config.popup_link_url || '/'}
            onClick={() => setVisible(false)}
            className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 active:scale-95"
            style={{ background: category.bg }}
          >
            Get More Details
            <ChevronRight size={16} />
          </Link>

          <button
            onClick={() => setVisible(false)}
            className="mt-2 w-full text-xs text-gray-400 hover:text-gray-600 transition-colors py-1"
          >
            No thanks, close
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes popup-in {
          from { opacity: 0; transform: scale(0.92) translateY(16px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-popup {
          animation: popup-in 0.28s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        }
      `}</style>
    </div>
  );
}
